import { PitchDetector } from "pitchy";

export const PITCH_ANALYSIS_WINDOW_SIZE = 4096;

const MIN_PITCH_CLARITY = 0.82;
const MIN_STABLE_FRAMES = 9;
const STABILITY_WINDOW_MS = 750;
const MIN_STABLE_DURATION_MS = 450;
const MAX_FRAME_GAP_MS = 140;
const MAX_CLUSTER_DISTANCE_SEMITONES = 0.55;
const MAX_MEDIAN_DEVIATION_SEMITONES = 0.3;

export type DetectedPitchFrame = {
  observedAt: number;
  frequencyHz: number;
  midi: number;
  clarity: number;
  rms: number;
};

export type StablePitch = {
  detectedMidi: number;
  clarity: number;
  sampleCount: number;
  durationMs: number;
};

export function frequencyToMidi(frequencyHz: number): number {
  return 69 + 12 * Math.log2(frequencyHz / 440);
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  const upper = sorted[middle];
  if (upper === undefined) throw new Error("PITCH_SAMPLES_REQUIRED");
  if (sorted.length % 2) return upper;
  const lower = sorted[middle - 1];
  if (lower === undefined) throw new Error("PITCH_SAMPLES_REQUIRED");
  return (lower + upper) / 2;
}

function rootMeanSquare(samples: Float32Array): number {
  let sumSquares = 0;
  for (const sample of samples) sumSquares += sample * sample;
  return Math.sqrt(sumSquares / samples.length);
}

function normalizeOctave(midi: number, referenceMidi: number): number {
  return midi + Math.round((referenceMidi - midi) / 12) * 12;
}

export function adaptiveRmsThreshold(ambientRms: number): number {
  if (!Number.isFinite(ambientRms) || ambientRms < 0) return 0.006;
  return Math.min(0.06, Math.max(0.006, ambientRms * 2.2));
}

export function summarizeStablePitch(frames: readonly DetectedPitchFrame[]): StablePitch | null {
  const lastFrame = frames.at(-1);
  if (!lastFrame) return null;
  const recent = frames.filter((frame) => lastFrame.observedAt - frame.observedAt <= STABILITY_WINDOW_MS);
  if (recent.length < MIN_STABLE_FRAMES) return null;
  let bestCluster: Array<DetectedPitchFrame & { normalizedMidi: number }> = [];
  for (const candidate of recent) {
    const cluster = recent
      .map((frame) => ({ ...frame, normalizedMidi: normalizeOctave(frame.midi, candidate.midi) }))
      .filter((frame) => Math.abs(frame.normalizedMidi - candidate.midi) <= MAX_CLUSTER_DISTANCE_SEMITONES);
    if (cluster.length > bestCluster.length) bestCluster = cluster;
  }
  if (bestCluster.length < MIN_STABLE_FRAMES || bestCluster.length < Math.ceil(recent.length * 0.75)) return null;
  const first = bestCluster[0];
  const last = bestCluster.at(-1);
  if (!first || !last) return null;
  const durationMs = Math.round(last.observedAt - first.observedAt);
  if (durationMs < MIN_STABLE_DURATION_MS) return null;
  for (let index = 1; index < bestCluster.length; index += 1) {
    const previous = bestCluster[index - 1];
    const current = bestCluster[index];
    if (!previous || !current || current.observedAt - previous.observedAt > MAX_FRAME_GAP_MS) return null;
  }
  const detectedMidi = median(bestCluster.map((frame) => frame.normalizedMidi));
  const medianDeviation = median(bestCluster.map((frame) => Math.abs(frame.normalizedMidi - detectedMidi)));
  if (medianDeviation > MAX_MEDIAN_DEVIATION_SEMITONES) return null;
  return {
    detectedMidi,
    clarity: median(bestCluster.map((frame) => frame.clarity)),
    sampleCount: bestCluster.length,
    durationMs
  };
}

export class PitchFrameEstimator {
  readonly #detector: PitchDetector<Float32Array>;

  public constructor(public readonly windowSize = PITCH_ANALYSIS_WINDOW_SIZE) {
    this.#detector = PitchDetector.forFloat32Array(windowSize);
    this.#detector.clarityThreshold = MIN_PITCH_CLARITY;
    this.#detector.minVolumeDecibels = -60;
  }

  public detect(samples: Float32Array, sampleRate: number, observedAt = 0, rmsFloor = 0.006): DetectedPitchFrame | null {
    if (samples.length !== this.windowSize || !Number.isFinite(sampleRate) || sampleRate <= 0) return null;
    const rms = rootMeanSquare(samples);
    if (rms < rmsFloor) return null;
    const [frequencyHz, clarity] = this.#detector.findPitch(samples, sampleRate);
    if (!Number.isFinite(frequencyHz) || frequencyHz < 65 || frequencyHz > 1_000 || clarity < MIN_PITCH_CLARITY) return null;
    return { observedAt, frequencyHz, midi: frequencyToMidi(frequencyHz), clarity, rms };
  }
}

export class MicrophonePitchCapture {
  #context: AudioContext | null = null;
  #stream: MediaStream | null = null;
  #source: MediaStreamAudioSourceNode | null = null;
  #highPass: BiquadFilterNode | null = null;
  #lowPass: BiquadFilterNode | null = null;
  #analyser: AnalyserNode | null = null;
  #animationFrame = 0;
  #lastEstimateAt = 0;

  public async start(onFrame: (frame: DetectedPitchFrame | null) => void): Promise<void> {
    if (this.#stream) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: { ideal: 1 },
        echoCancellation: { ideal: false },
        noiseSuppression: { ideal: false },
        autoGainControl: { ideal: false }
      },
      video: false
    });
    const context = new window.AudioContext();
    const source = context.createMediaStreamSource(stream);
    const highPass = context.createBiquadFilter();
    highPass.type = "highpass";
    highPass.frequency.value = 65;
    highPass.Q.value = 0.7;
    const lowPass = context.createBiquadFilter();
    lowPass.type = "lowpass";
    lowPass.frequency.value = 1_200;
    lowPass.Q.value = 0.7;
    const analyser = context.createAnalyser();
    analyser.fftSize = PITCH_ANALYSIS_WINDOW_SIZE;
    analyser.smoothingTimeConstant = 0;
    source.connect(highPass);
    highPass.connect(lowPass);
    lowPass.connect(analyser);
    await context.resume();
    const samples = new Float32Array(analyser.fftSize);
    const estimator = new PitchFrameEstimator(analyser.fftSize);
    this.#context = context;
    this.#stream = stream;
    this.#source = source;
    this.#highPass = highPass;
    this.#lowPass = lowPass;
    this.#analyser = analyser;
    const ambientSamples: number[] = [];
    for (let index = 0; index < 10; index += 1) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
      if (!this.#analyser) return;
      analyser.getFloatTimeDomainData(samples);
      ambientSamples.push(rootMeanSquare(samples));
    }
    const rmsFloor = adaptiveRmsThreshold(median(ambientSamples));
    const loop = (now: number) => {
      if (!this.#analyser) return;
      if (now - this.#lastEstimateAt >= 55) {
        this.#lastEstimateAt = now;
        this.#analyser.getFloatTimeDomainData(samples);
        onFrame(estimator.detect(samples, context.sampleRate, now, rmsFloor));
      }
      this.#animationFrame = window.requestAnimationFrame(loop);
    };
    this.#animationFrame = window.requestAnimationFrame(loop);
  }

  public async stop(): Promise<void> {
    window.cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = 0;
    this.#analyser?.disconnect();
    this.#lowPass?.disconnect();
    this.#highPass?.disconnect();
    this.#source?.disconnect();
    for (const track of this.#stream?.getTracks() ?? []) track.stop();
    this.#analyser = null;
    this.#lowPass = null;
    this.#highPass = null;
    this.#source = null;
    this.#stream = null;
    const context = this.#context;
    this.#context = null;
    if (context && context.state !== "closed") await context.close();
  }
}
